'use client'

import React from 'react'
import { ScanSearch, Sparkles, MessageCircle, RefreshCw } from 'lucide-react'

/**
 * Radar Fix Loop Diagrams
 * Visual support for "How Radar Fix Prompts and the AI Advisor Fix Your AEO".
 * Theming per CLAUDE.md: data-theme='dark', no dark: variants.
 * Brand: #008dcc #FDC304 #F90B8A #8b5cf6.
 */

interface Step {
  n: number
  title: string
  desc: string
  color: string
  icon: React.ReactNode
}

const STEPS: Step[] = [
  {
    n: 1,
    title: 'Audit',
    desc: 'Radar scores the page and turns every finding into an action item.',
    color: '#008dcc',
    icon: <ScanSearch size={18} aria-hidden />,
  },
  {
    n: 2,
    title: 'Fix prompt',
    desc: 'Each action item generates a prompt pre-filled with your audit data. Paste it into Claude or Cursor and ship the fix.',
    color: '#8b5cf6',
    icon: <Sparkles size={18} aria-hidden />,
  },
  {
    n: 3,
    title: 'Advisor',
    desc: 'Ask the AI advisor the judgment calls a static report cannot answer.',
    color: '#FDC304',
    icon: <MessageCircle size={18} aria-hidden />,
  },
  {
    n: 4,
    title: 'Re-audit',
    desc: 'Re-run the audit to confirm the fix landed. The score moves, the finding clears.',
    color: '#F90B8A',
    icon: <RefreshCw size={18} aria-hidden />,
  },
]

export function RadarFixLoopDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-10 rounded-2xl border border-border/60 bg-muted/50 p-6 md:p-8'
    >
      <div className='mb-6'>
        <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          The audit-to-fix loop
        </p>
        <p className='mt-1 text-sm leading-relaxed text-foreground/80'>
          A failing score becomes a checklist. Each pass clears findings and
          moves the grade.
        </p>
      </div>

      <div className='grid gap-3 sm:grid-cols-2'>
        {STEPS.map(s => (
          <div
            key={s.n}
            className='rounded-xl border border-border/60 bg-background p-5'
            style={{ borderTopWidth: '4px', borderTopColor: s.color }}
          >
            <div className='flex items-center gap-2'>
              <div
                className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold'
                style={{ backgroundColor: `${s.color}15`, color: s.color }}
              >
                {s.n}
              </div>
              <span style={{ color: s.color }}>{s.icon}</span>
              <span className='text-base font-semibold leading-none text-foreground'>
                {s.title}
              </span>
            </div>
            <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
              {s.desc}
            </p>
          </div>
        ))}
      </div>

      <div
        className='mt-5 rounded-lg p-4'
        style={{ backgroundColor: '#008dcc08', border: '1px solid #008dcc20' }}
      >
        <p className='text-xs leading-relaxed text-foreground/80'>
          <span className='font-semibold' style={{ color: '#008dcc' }}>
            It is a loop, not a one-time pass.
          </span>{' '}
          Audit, fix with a prompt, ask the advisor, re-audit, repeat until the
          page is answer-ready.
        </p>
      </div>
    </div>
  )
}
